import { Autocomplete, InputBase, Popper, useTheme } from '@mui/material';
import { EEntityType, entityTypeToLink, entityTypeToString } from '@sharedInterfaces/globalEnums';
import type { ISearchSuggestions } from '@sharedInterfaces/ICompany';
// Close-Icon wird nicht mehr benötigt
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import getSearchSuggestions from '@src/APIs/graphQl/Company/getSearchSuggestions';
import { LoadingBar } from '@src/App/Components/Core/Layouts/LayoutElements/ContentHeader/ContentHeader';
import { entityTypeToIcon } from '@src/helper/tsxHelper';
import store, { type AppState } from '@store/store';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { MAX_WIDTH, TYPING_TIMEOUT } from './SearchBox';
import styles from './SearchBox.module.css';

export interface SearchInputProps {
    open: boolean;
    searchValue: string;
    setSearchValue: (value: string) => void;
    setOpen: (value: boolean) => void;
    customColor?: string;
    inputRef?: React.RefObject<HTMLInputElement>;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    isMobile?: boolean;
    containSuggestions?: boolean;
}

export function SearchInput({
    open,
    searchValue,
    setSearchValue,
    setOpen,
    customColor,
    inputRef,
    onKeyDown,
    isMobile = false,
    containSuggestions = false,
}: SearchInputProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    // Verwende einen festen String für das aria-label

    const [typedValue, setTypedValue] = useState<string>('');
    const [suggestions, setSuggestions] = useState<ISearchSuggestions[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const fullState = store.getState();

    useEffect(() => {
        if (searchValue && !loading) {
            setLoading(true);
            getSearchSugs(searchValue);
        }

        function getSearchSugs(searchedValue: string) {
            const localSuggestions: ISearchSuggestions[] = getLocalSuggestions(searchValue, fullState);
            setSuggestions(localSuggestions);

            getSearchSuggestions(searchedValue, (onlineSuggestions) => {
                const actualSuggestions = [...localSuggestions];
                onlineSuggestions.forEach((suggestion) => {
                    if (!actualSuggestions.some((s) => s.id === suggestion.id && s.type === suggestion.type)) {
                        actualSuggestions.push(suggestion);
                    }
                });
                actualSuggestions.sort(sortSuggestions);

                setSuggestions(actualSuggestions);
                if (searchedValue !== searchValue) {
                    getSearchSugs(searchedValue);
                }
            })
                .catch((error) => {
                    console.error('Error fetching suggestions:', error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [searchValue]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchValue(typedValue);
        }, TYPING_TIMEOUT);

        return () => clearTimeout(timer);
    }, [typedValue]);

    // Fokus wird in SearchBox.tsx gehandhabt

    const onSelect = (selectedOption: ISearchSuggestions) => {
        navigate(entityTypeToLink(selectedOption.id, selectedOption.type));
        setSearchValue('');
        setOpen(false);
    };

    return (
        <>
            <Autocomplete
                id="skillSelect"
                value={typedValue}
                size="small"
                style={{ width: '100%' }}
                onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                        setTypedValue(newValue);
                    } else if (newValue) {
                        onSelect(newValue);
                    }
                }}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                options={suggestions}
                getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.id) return option.label;
                    return 'unknown';
                }}
                renderOption={(props, option) => {
                    const icon = entityTypeToIcon(option.type);
                    return (
                        <li
                            {...props}
                            key={`${option.type}-${option.id}-${option.label}`}
                            style={{ borderBottom: 'solid 1px #ddd' }}
                        >
                            <div className={styles.suggestion}>
                                {icon && <div className={styles.icon}>{icon}</div>}
                                <div className={styles.suggestionTexts}>
                                    <span className={styles.label}>{option.label}</span>
                                    <span className={styles.type}>{entityTypeToString(lang, option.type)}</span>
                                </div>
                            </div>
                        </li>
                    );
                }}
                sx={{ width: '100%' }}
                freeSolo
                renderInput={(params) => (
                    <InputBase
                        ref={params.InputProps.ref}
                        inputRef={inputRef}
                        placeholder=""
                        onChange={(event) => setTypedValue(event.target.value)}
                        onKeyDown={(e) => {
                            onKeyDown?.(e);
                            if (e.key === 'Enter' && suggestions.length > 0) {
                                onSelect(suggestions[0]);
                            }
                        }}
                        inputProps={{
                            ...params.inputProps,
                            style: { padding: '0 8px' },
                        }}
                        fullWidth
                    />
                )}
                PopperComponent={(props) => {
                    // For desktop view or mobile view without containSuggestions, use themeContainer
                    // For mobile view with containSuggestions, use mobileSearchContainer
                    if (containSuggestions && isMobile) {
                        return (
                            <Popper
                                {...props}
                                container={document.getElementById('mobileSearchContainer')}
                                style={{ width: '100%' }}
                            />
                        );
                    }
                    return <Popper {...props} container={document.getElementById('themeContainer')} />;
                }}
                componentsProps={{
                    popper: {
                        sx: {
                            zIndex: containSuggestions ? 1 : 2000,
                            minWidth: isMobile ? '100%' : MAX_WIDTH,
                            width: isMobile ? '100%' : 'auto',
                            border: 'none',
                            boxShadow: theme.shadows[2],
                            ...(containSuggestions &&
                                isMobile && {
                                    maxHeight: 'calc(100vh - 200px)',
                                    width: '100%',
                                    borderRadius: '8px', // Abgerundete Ecken für die Vorschläge
                                    overflow: 'hidden', // Verhindert, dass Inhalte über die abgerundeten Ecken hinausragen
                                    marginTop: '8px', // Minimaler Abstand zum InputField
                                }),
                        },
                    },
                }}
            />

            {/* Close Button */}
            {/* Close Button wird in SearchBox.tsx gehandhabt */}

            {loading && <LoadingBar />}
        </>
    );
}

function getLocalSuggestions(searchValue: string, fullState: AppState) {
    const localSuggestions: ISearchSuggestions[] = [];
    const searchValueLower = searchValue.toLowerCase();

    const addSuggestions = (items: { id: number; title: string }[], entityType: EEntityType) => {
        items.forEach((item) => {
            if (item.title.toLowerCase().includes(searchValueLower)) {
                localSuggestions.push({ id: item.id, label: item.title, type: entityType });
            }
        });
    };

    addSuggestions(fullState.company.allEmployees, EEntityType.EMPLOYEE);
    addSuggestions(fullState.OUs, EEntityType.ORGANISATIONAL_UNIT);
    addSuggestions(fullState.company.allRoles, EEntityType.ROLE);
    addSuggestions(fullState.company.allCompetences, EEntityType.COMPETENCE);
    addSuggestions(fullState.products, EEntityType.PRODUCT);
    addSuggestions(fullState.company.allSkills, EEntityType.SKILL);
    addSuggestions(fullState.company.allCertificates, EEntityType.CERTIFICATE);
    addSuggestions(fullState.businesses, EEntityType.BUSINESS);
    addSuggestions(fullState.projects, EEntityType.PROJECT);
    addSuggestions(fullState.opportunity, EEntityType.OPPORTUNITY);

    return localSuggestions;
}

const typeOrder: Record<string, number> = {
    [EEntityType.EMPLOYEE]: 1,
    [EEntityType.ORGANISATIONAL_UNIT]: 2,
    [EEntityType.ROLE]: 3,
    [EEntityType.COMPETENCE]: 4,
    [EEntityType.PRODUCT]: 5,
    [EEntityType.SKILL]: 6,
    [EEntityType.CERTIFICATE]: 7,
    [EEntityType.BUSINESS]: 8,
    [EEntityType.PROJECT]: 9,
    [EEntityType.OPPORTUNITY]: 10,
};

function sortSuggestions(a: ISearchSuggestions, b: ISearchSuggestions): number {
    if (a.score === undefined && b.score !== undefined) return 1;
    if (a.score !== undefined && b.score === undefined) return 0;
    if (a.score !== undefined && b.score !== undefined && a.score !== b.score) {
        return a.score - b.score;
    }
    if (typeOrder[a.type] !== typeOrder[b.type]) {
        return typeOrder[a.type] - typeOrder[b.type];
    }
    return a.label.localeCompare(b.label);
}
